/**
 * Tiroir de fiche (UAT Olivier 10.10.2026) — « mettre en modal la page de prise de rendez-vous, et de
 * manière globale sur le site ».
 *
 * Un magasin minuscule (useSyncExternalStore) et un registre de résolveurs : un module enregistre la
 * fonction qui, pour un chemin de fiche (`/portal/{ws}/quenotte/appointment/new?date=…`), rend le composant
 * de page capable de s'afficher « intégré » (sans chrome, sans navigation). `FormDrawerHost`, monté une fois
 * dans les fournisseurs du client, affiche ce composant dans un RightDrawer. Un chemin qu'aucun résolveur ne
 * reconnaît reste une navigation classique (`openFormDrawerOrNavigate`).
 *
 * Contrat du composant intégré : il lit `pathname` / `search` au lieu de l'URL, appelle `onSaved` après un
 * enregistrement réussi (ou une suppression) et `onCancel` sur Annuler ; toute autre navigation qu'il
 * déclenche ferme le tiroir (le hôte observe la route).
 */
import { useSyncExternalStore } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

export interface EmbeddedFormSavedMeta {
  /** Avertissements « hors présence » calculés par la fiche de rendez-vous (UPT-2035 point 3). */
  presenceWarnings?: string[];
}

export interface EmbeddedFormProps {
  embedded: true;
  pathname: string;
  search: string;
  onSaved: (record?: unknown, meta?: EmbeddedFormSavedMeta) => void;
  onCancel: () => void;
}

export type EmbeddedFormComponent =
  | ComponentType<EmbeddedFormProps>
  | LazyExoticComponent<ComponentType<EmbeddedFormProps>>;

export type FormDrawerResolver = (pathname: string) => EmbeddedFormComponent | null;

export interface FormDrawerOptions {
  /** Titre du tiroir ; sinon la clé i18n `core.formDrawer.title`. */
  title?: string;
  /** Après un enregistrement réussi (le tiroir se ferme ensuite). */
  onSaved?: (record?: unknown, meta?: EmbeddedFormSavedMeta) => void;
  /** À la fermeture, quelle qu'en soit la raison. */
  onClosed?: () => void;
}

export interface FormDrawerState {
  open: boolean;
  path: string;
  title?: string;
  onSaved?: FormDrawerOptions['onSaved'];
  onClosed?: FormDrawerOptions['onClosed'];
  /** Incrémenté à chaque ouverture : remonte le composant même pour le même chemin. */
  key: number;
}

const CLOSED: FormDrawerState = { open: false, path: '', key: 0 };

let state: FormDrawerState = CLOSED;
const listeners = new Set<() => void>();
const resolvers: FormDrawerResolver[] = [];

const emit = () => {
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => state;

/** Un module déclare les chemins de fiche qu'il sait afficher en tiroir (idempotent). */
export function registerFormDrawerResolver(resolver: FormDrawerResolver): void {
  if (!resolvers.includes(resolver)) resolvers.push(resolver);
}

/** `path` = chemin + éventuelle chaîne de requête, sans origine. */
export function splitFormDrawerPath(path: string): { pathname: string; search: string } {
  const trimmed = (path ?? '').trim();
  const cut = trimmed.indexOf('?');
  if (cut < 0) return { pathname: trimmed, search: '' };
  return { pathname: trimmed.slice(0, cut), search: trimmed.slice(cut) };
}

export function resolveFormDrawerComponent(path: string): EmbeddedFormComponent | null {
  const { pathname } = splitFormDrawerPath(path);
  if (pathname === '') return null;
  for (const resolver of resolvers) {
    const component = resolver(pathname);
    if (component) return component;
  }
  return null;
}

export const canOpenInFormDrawer = (path: string): boolean => resolveFormDrawerComponent(path) != null;

export function openFormDrawer(path: string, options: FormDrawerOptions = {}): void {
  state = {
    open: true,
    path,
    title: options.title,
    onSaved: options.onSaved,
    onClosed: options.onClosed,
    key: state.key + 1,
  };
  emit();
}

export function closeFormDrawer(): void {
  if (!state.open) return;
  const closed = state.onClosed;
  state = { ...state, open: false, onSaved: undefined, onClosed: undefined };
  emit();
  closed?.();
}

/** Tiroir si un module sait afficher ce chemin, sinon navigation classique (adoption progressive). */
export function openFormDrawerOrNavigate(
  path: string,
  navigate: (to: string) => unknown,
  options: FormDrawerOptions = {}
): boolean {
  if (canOpenInFormDrawer(path)) {
    openFormDrawer(path, options);
    return true;
  }
  void navigate(path);
  return false;
}

export function useFormDrawerState(): FormDrawerState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Tests seulement. */
export function resetFormDrawerForTests(): void {
  state = CLOSED;
  resolvers.length = 0;
  emit();
}
